<template>
  <div v-if="filteredResults?.length ?? 0 > 0" class="border-l border-transparent">
    {{ $t('search.results', filteredResults?.length ?? 0) }}
  </div>

  <div v-if="filteredResults?.length ?? 0 > 0">
    <TransitionGroup tag="div" name="results" class="results-grid">
      <div class="results-header text-h2 max-lg:hidden">
        {{ $t('search.type') }}
      </div>
      <div class="results-header text-h2 max-lg:hidden">
        {{ $t('search.designation') }}
      </div>
      <div class="results-header text-h2 max-lg:hidden" />
      <div v-if="filteredResults.length === 0" class="col-span-5 py-8 text-center">
        {{ $t('search.results', 0) }}
      </div>
      <template v-for="hit in filteredResults" :key="hit.refIndex">
        <div class="pr-4 lg:pr-10">
          {{ $t(`search.blueprint.${hit.item.type}`) }}
        </div>
        <div class="pr-4 lg:pr-10">
          <h3 class="typo-p2 mb-2 !font-semibold" v-html="useFormat(hit.item.title)" />
          <!-- <template v-for="(highlightResult, index) in createMatchGroups(hit)" :key="index">
            <p class="highlight max-w-text not-last:mb-2 line-clamp-3 text-sm" v-html="highlightResult" />
          </template> -->
        </div>
        <ul class="max-lg:mt-5 max-lg:flex max-lg:flex-wrap max-lg:gap-x-4 max-lg:gap-y-2 lg:pr-10">
          <li>
            <UiButton variant="secondary" icon="arrow-right" :to="localePath(hit.item.url)">{{ $t('search.to_page') }}
            </UiButton>
          </li>
        </ul>
      </template>
      <hr class="mt-10 mb-4 last:hidden lg:hidden" />
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
import type { FuseResult } from 'fuse.js'
import {
  FuseInjectionKey,
  type OfflineSearchResult,
} from '~/composables/useInjectableFuse'

const props = defineProps<{
  searchQuery: string
}>()

const localePath = useLocalePath()

const searchQuery = computed(() => props.searchQuery)

const fuse = inject(FuseInjectionKey)

const results = ref<FuseResult<OfflineSearchResult>[]>()

const debouncedRefresh = useDebounceFn(
  () => {
    results.value = fuse?.value?.search(searchQuery.value)
  },
  250,
  { maxWait: 1100 },
)
watch(searchQuery, debouncedRefresh)

const filteredResults = computed<FuseResult<OfflineSearchResult>[]>(() => {
  if (!results.value) return []

  let filteredHits = results.value

  const hitInSpecialTitle = filteredHits.findIndex((hit) => {
    return searchQuery.value.length > 3
      && ['medication', 'active_ingredient'].includes(hit.item.type ?? '')
      && hit.item.title.toLowerCase().includes(searchQuery.value.toLowerCase())
  }) !== -1
  if (hitInSpecialTitle) {
    // filter out all other hits in text
    filteredHits = filteredHits.filter((hit: any) => {
      return hit.item.title?.toLowerCase().includes(searchQuery.value.toLowerCase())
    })
  }

  const desitinProductFound = filteredHits.findIndex((hit: any) => {
    return hit.item.title?.toLowerCase().includes('desitin')
  }) !== -1

  if (desitinProductFound) {
    filteredHits.sort((a: any, b: any) => {
      const aIsDesitin = a.item.title?.toLowerCase().indexOf('desitin') !== -1
      const bIsDesitin = b.item.title?.toLowerCase().indexOf('desitin') !== -1
      return (bIsDesitin ? 1 : 0) - (aIsDesitin ? 1 : 0)
    })
  }

  return filteredHits
})

const createMatchGroups = (result: FuseResult<OfflineSearchResult>) => {
  if (!result.matches) return
  return result.matches.slice(0).map((match) => {
    let html = ''
    let index = 0

    match.indices.forEach((range) => {
      if (!match.value) return
      html += match.value.slice(index, range[0]) ?? ''
      html += `<em>${match.value.slice(range[0], range[1] + 1)}</em>`
      index = range[1] + 1
    })
    if (!match.value) return

    html += match.value.slice(index)

    return html
  })
}
</script>

<style scoped>
@import 'tailwindcss/theme' theme(reference);
@import '../../assets/css/_colors.css' theme(reference);

.results-grid {
  @apply grid;
  position: relative;
  overflow-x: hidden;
  grid-template-columns: 2fr 5fr 1fr;
  grid-template-rows: auto;
  gap: 2rem 0rem;
  padding-top: 1.5rem;

  &>*:not(.results-header) {
    position: relative;

    &::before {
      @apply max-lg:hidden;
      content: '';
      position: absolute;
      border-top: 1px solid black;
      opacity: 0.2;
      z-index: 1;

      inline-size: 100%;
      inset-inline-start: 0rem;
      inset-block-start: -1rem;
    }
  }
}

.results-move,
/* apply transition to moving elements */
.results-enter-active,
.results-leave-active {
  transition: all 0.5s ease;
}

.results-enter-from,
.results-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

/* ensure leaving items are taken out of layout flow so that moving
   animations can be calculated correctly. */
.results-leave-active {
  position: absolute;
}

:deep(.highlight) em {
  @apply relative z-0 font-medium not-italic after:absolute after:-inset-x-px after:inset-y-0 after:-z-10 after:bg-Blue-Primary/45;
}
</style>
